import { Icons } from '@/app/components/ui/Icons'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { FC, PropsWithChildren, useEffect, useRef } from 'react'

export interface Props extends PropsWithChildren {
	isOpen: boolean
	className?: string
	fullScreenMode?: boolean
	animationDuration?: number
	headerText?: string
	closeButton?: boolean
	disableOutsideClick?: boolean
	onClose: () => void
}

const Popup: FC<Props> = ({
	isOpen,
	className,
	children,
	fullScreenMode,
	animationDuration = 0.2,
	headerText,
	closeButton,
	disableOutsideClick = false,
	onClose,
}) => {
	const ref = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (disableOutsideClick) return

		const handleClickOutside = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				onClose()
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [onClose, disableOutsideClick])

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					ref={ref}
					className={classNames(className, 'popup-container absolute z-30')}
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -20 }}
					transition={{ duration: animationDuration }}
				>
					{headerText && (
						<div className="popup-header mb-4 bg-white flex justify-start items-center">
							{closeButton && (
								<button className="p-2 -ms-2 -my-2" onClick={onClose}>
									<Icons.iconClose />
								</button>
							)}
							<span className="font-bold text-black text-xl ms-6">
								{headerText}
							</span>
						</div>
					)}

					{children}
				</motion.div>
			)}
		</AnimatePresence>
	)
}

export default Popup
